import React from 'react'

import { HospitalDetailProvider } from './hospital-detail-context'
import { HospitalDetailPhotoSlider as HospitalDetailPhotoSliderImpl } from './components/hospital-detail-photo-slider'
import { HospitalDetailHeader as HospitalDetailHeaderImpl } from './components/hospital-detail-header'
import { HospitalDetailTabNavigation as HospitalDetailTabNavigationImpl } from './components/hospital-detail-tab-navigation'
import { HospitalDetailIntroduction as HospitalDetailIntroductionImpl } from './components/hospital-detail-introduction'
import { HospitalDetailDoctors as HospitalDetailDoctorsImpl } from './components/hospital-detail-doctors'
import { HospitalDetailEvents as HospitalDetailEventsImpl } from './components/hospital-detail-events'
import { HospitalDetailReviews as HospitalDetailReviewsImpl } from './components/hospital-detail-reviews'
import { HospitalDetailAccess as HospitalDetailAccessImpl } from './components/hospital-detail-access'
import { HospitalDetailOfficeHours as HospitalDetailOfficeHoursImpl } from './components/hospital-detail-office-hours'
import { HospitalDetailPaymentOptions as HospitalDetailPaymentOptionsImpl } from './components/hospital-detail-payment-options'
import { HospitalDetailCTAButtons as HospitalDetailCTAButtonsImpl } from './components/hospital-detail-cta-buttons'

import type { HospitalDetailTemplateProps, HospitalDetailModels } from './types'

const HospitalDetailPhotoSlider = ({ children }: { children?: React.ReactNode }) =>
  children ? <div className="hospital-detail-photo-slider">{children}</div> : <HospitalDetailPhotoSliderImpl />

const HospitalDetailHeader = ({ children }: { children?: React.ReactNode }) =>
  children ? <div className="hospital-detail-header">{children}</div> : <HospitalDetailHeaderImpl />

const HospitalDetailTabNavigation = ({ children }: { children?: React.ReactNode }) =>
  children ? <div className="hospital-detail-tab-navigation">{children}</div> : <HospitalDetailTabNavigationImpl />

const HospitalDetailIntroduction = ({ children }: { children?: React.ReactNode }) =>
  children ? <div className="hospital-detail-introduction">{children}</div> : <HospitalDetailIntroductionImpl />

const HospitalDetailDoctors = ({ children }: { children?: React.ReactNode }) =>
  children ? <div className="hospital-detail-doctors">{children}</div> : <HospitalDetailDoctorsImpl />

const HospitalDetailEvents = ({ children }: { children?: React.ReactNode }) =>
  children ? <div className="hospital-detail-events">{children}</div> : <HospitalDetailEventsImpl />

const HospitalDetailReviews = ({ children }: { children?: React.ReactNode }) =>
  children ? <div className="hospital-detail-reviews">{children}</div> : <HospitalDetailReviewsImpl />

const HospitalDetailBadges = ({ children }: { children?: React.ReactNode }) => (
  <div className="hospital-detail-badges">{children || 'Badges - To be implemented'}</div>
)

const HospitalDetailAccess = ({ children }: { children?: React.ReactNode }) =>
  children ? <div className="hospital-detail-access">{children}</div> : <HospitalDetailAccessImpl />

const HospitalDetailOfficeHours = ({ children }: { children?: React.ReactNode }) =>
  children ? <div className="hospital-detail-office-hours">{children}</div> : <HospitalDetailOfficeHoursImpl />

const HospitalDetailPaymentOptions = ({ children }: { children?: React.ReactNode }) =>
  children ? <div className="hospital-detail-payment-options">{children}</div> : <HospitalDetailPaymentOptionsImpl />

const HospitalDetailCTAButtons = ({ children }: { children?: React.ReactNode }) =>
  children ? <div className="hospital-detail-cta-buttons">{children}</div> : <HospitalDetailCTAButtonsImpl />

/**
 * Hospital Detail Template - Main component with Compound Component pattern
 *
 * Example usage:
 * <HospitalDetailTemplate user={user} models={models} {...eventHandlers}>
 *   <HospitalDetailTemplate.PhotoSlider />
 *   <HospitalDetailTemplate.Header />
 *   <HospitalDetailTemplate.TabNavigation />
 *   <HospitalDetailTemplate.Introduction />
 *   <HospitalDetailTemplate.Doctors />
 *   <HospitalDetailTemplate.Events />
 *   <HospitalDetailTemplate.Reviews />
 *   <HospitalDetailTemplate.Badges />
 *   <HospitalDetailTemplate.Access />
 *   <HospitalDetailTemplate.OfficeHours />
 *   <HospitalDetailTemplate.PaymentOptions />
 *   <HospitalDetailTemplate.CTAButtons />
 * </HospitalDetailTemplate>
 */
export const HospitalDetailTemplate = ({
  user,
  models,
  optionalMarketingItems,
  onClickTab,
  onClickMoreList,
  onClickCopyAddress,
  onClickMap,
  onClickConsultation,
  onClickChatConsultation,
  children,
}: HospitalDetailTemplateProps & { children: React.ReactNode }) => {
  return (
    <HospitalDetailProvider
      user={user}
      models={models}
      optionalMarketingItems={optionalMarketingItems}
      onClickTab={onClickTab}
      onClickMoreList={onClickMoreList}
      onClickCopyAddress={onClickCopyAddress}
      onClickMap={onClickMap}
      onClickConsultation={onClickConsultation}
      onClickChatConsultation={onClickChatConsultation}
    >
      <div className="hospital-detail-template">{children}</div>
    </HospitalDetailProvider>
  )
}

// Attach sub-components to main component (Compound Component pattern)
HospitalDetailTemplate.PhotoSlider = HospitalDetailPhotoSlider
HospitalDetailTemplate.Header = HospitalDetailHeader
HospitalDetailTemplate.TabNavigation = HospitalDetailTabNavigation
HospitalDetailTemplate.Introduction = HospitalDetailIntroduction
HospitalDetailTemplate.Doctors = HospitalDetailDoctors
HospitalDetailTemplate.Events = HospitalDetailEvents
HospitalDetailTemplate.Reviews = HospitalDetailReviews
HospitalDetailTemplate.Badges = HospitalDetailBadges
HospitalDetailTemplate.Access = HospitalDetailAccess
HospitalDetailTemplate.OfficeHours = HospitalDetailOfficeHours
HospitalDetailTemplate.PaymentOptions = HospitalDetailPaymentOptions
HospitalDetailTemplate.CTAButtons = HospitalDetailCTAButtons

// Default export for backward compatibility during migration
export default HospitalDetailTemplate

// Export types for external usage
export type {
  HospitalDetailTemplateProps,
  HospitalDetailModels,
  HospitalDetailContextValue,
  HospitalDetailDataContextValue,
  HospitalDetailActionsContextValue,
  ExtendTabItem,
  HospitalDetailAnalytics,
} from './types'

// Export context hooks
export { useHospitalDetailContext, useHospitalDetailData, useHospitalDetailActions } from './hospital-detail-context'

// Export constants
export { CCTV_BADGE_CODE, TAB_HEIGHT } from './types'
